import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarIcon, Download } from "lucide-react";

type SummaryRow = {
  id: string;
  driver_name: string;
  week_start: string;
  week_end: string;
  jobs: number;
  gross: number;
  gst_amount: number;
  commission: number;
  bonus: number;
  total_deductions: number;
  total_payment: number;
  deduction_lines: { label: string; amount: number }[] | null;
};

const money = (n: number | null | undefined) => `$${Number(n || 0).toFixed(2)}`;
const ddmmyy = (ymd?: string | null) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  if (!y || !m || !d) return ymd;
  return `${d}-${m}-${y.slice(-2)}`;
};

const startOfYear = () => {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
};
const today = () => new Date().toISOString().slice(0, 10);

interface Props {
  driverName: string;
}

const DriverFullReporting: React.FC<Props> = ({ driverName }) => {
  const [from, setFrom] = useState<string>(startOfYear());
  const [to, setTo] = useState<string>(today());
  const [rows, setRows] = useState<SummaryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from("driver_weekly_summaries")
        .select(
          "id, driver_name, week_start, week_end, jobs, gross, gst_amount, commission, bonus, total_deductions, total_payment, deduction_lines"
        )
        .eq("driver_name", driverName)
        .gte("week_start", from)
        .lte("week_start", to)
        .order("week_start", { ascending: true });
      if (error) throw error;
      setRows((data as any[]) || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load summaries");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverName]);

  // Build a stable, ordered list of every distinct deduction label across rows
  // so each deduction gets its own column. Credits (negative amounts) are kept
  // as their own column too so nothing is hidden.
  const deductionCols = useMemo(() => {
    const set: string[] = [];
    rows.forEach((r) => {
      (r.deduction_lines || []).forEach((l) => {
        if (!set.includes(l.label)) set.push(l.label);
      });
    });
    return set;
  }, [rows]);

  const valueFor = (r: SummaryRow, label: string): number | null => {
    const line = (r.deduction_lines || []).find((l) => l.label === label);
    return line ? line.amount : null;
  };

  // Deduction lines are signed (positive = deducted, negative = a
  // credit/extra payment -- see PayslipDeductionLine in lib/payslip.ts).
  // Split each row's lines into a gross "Deductions" total and a gross
  // "Payments" total so the driver can see both at a glance, alongside the
  // existing per-label breakdown columns which show the detail.
  const deductionsFor = (r: SummaryRow) =>
    (r.deduction_lines || []).filter((l) => l.amount > 0).reduce((sum, l) => sum + l.amount, 0);
  const paymentsFor = (r: SummaryRow) =>
    (r.deduction_lines || []).filter((l) => l.amount < 0).reduce((sum, l) => sum + Math.abs(l.amount), 0);

  const totals = useMemo(() => {
    const base = {
      jobs: 0,
      gross: 0,
      gst: 0,
      commission: 0,
      bonus: 0,
      deductions: 0,
      payments: 0,
      payment: 0,
    };
    const ded: Record<string, number> = {};
    deductionCols.forEach((c) => (ded[c] = 0));
    rows.forEach((r) => {
      base.jobs += Number(r.jobs || 0);
      base.gross += Number(r.gross || 0);
      base.gst += Number(r.gst_amount || 0);
      base.commission += Number(r.commission || 0);
      base.bonus += Number(r.bonus || 0);
      base.deductions += deductionsFor(r);
      base.payments += paymentsFor(r);
      base.payment += Number(r.total_payment || 0);
      deductionCols.forEach((c) => {
        const v = valueFor(r, c);
        if (v != null) ded[c] += v;
      });
    });
    return { base, ded };
  }, [rows, deductionCols]);

  const downloadCsv = () => {
    const header = [
      "Week Start",
      "Week End",
      "Jobs",
      "Gross",
      "Less GST",
      "Less Commission",
      "Bonus",
      "Deductions",
      "Payments",
      ...deductionCols,
      "Total Payment",
    ];
    const esc = (v: any) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.map(esc).join(",")];
    rows.forEach((r) => {
      lines.push(
        [
          ddmmyy(r.week_start),
          ddmmyy(r.week_end),
          r.jobs,
          Number(r.gross).toFixed(2),
          Number(r.gst_amount).toFixed(2),
          Number(r.commission).toFixed(2),
          Number(r.bonus).toFixed(2),
          deductionsFor(r).toFixed(2),
          paymentsFor(r).toFixed(2),
          ...deductionCols.map((c) => {
            const v = valueFor(r, c);
            return v == null ? "" : v.toFixed(2);
          }),
          Number(r.total_payment).toFixed(2),
        ]
          .map(esc)
          .join(",")
      );
    });
    lines.push(
      [
        "TOTAL",
        "",
        totals.base.jobs,
        totals.base.gross.toFixed(2),
        totals.base.gst.toFixed(2),
        totals.base.commission.toFixed(2),
        totals.base.bonus.toFixed(2),
        totals.base.deductions.toFixed(2),
        totals.base.payments.toFixed(2),
        ...deductionCols.map((c) => totals.ded[c].toFixed(2)),
        totals.base.payment.toFixed(2),
      ]
        .map(esc)
        .join(",")
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${driverName.replace(/\s+/g, "_")}_Full_Report_${from}_to_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end justify-between">
        <div>
          <div className="text-lg font-semibold">Full Reporting</div>
          <div className="text-sm text-muted-foreground">
            Choose a date range to report your month, quarter or full year — including deductions.
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex flex-col">
            <label className="text-xs text-muted-foreground mb-1">From</label>
            <div className="flex items-center gap-1">
              <CalendarIcon className="w-4 h-4 text-primary" />
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[160px] bg-background" />
            </div>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-muted-foreground mb-1">To</label>
            <div className="flex items-center gap-1">
              <CalendarIcon className="w-4 h-4 text-primary" />
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[160px] bg-background" />
            </div>
          </div>
          <Button onClick={load} className="bg-yellow-600 hover:bg-yellow-700 text-black">Run</Button>
          <Button onClick={downloadCsv} variant="outline" className="border-yellow-600 text-yellow-400 hover:bg-yellow-900">
            <Download className="w-4 h-4 mr-2" />
            CSV
          </Button>
        </div>
      </div>

      <div className="overflow-hidden border border-border rounded-lg">
        <div className="max-h-[72vh] overflow-auto">
          <table className="min-w-[820px] w-full text-xs">
            <thead className="sticky top-0 z-10 bg-neutral-900/95 backdrop-blur">
              <tr className="text-center">
                <th className="px-2 py-2 text-left">Week Start</th>
                <th className="px-2 py-2 text-left">Week End</th>
                <th className="px-2 py-2">Jobs</th>
                <th className="px-2 py-2 text-right">Gross</th>
                <th className="px-2 py-2 text-right">Less GST</th>
                <th className="px-2 py-2 text-right">Less Commission</th>
                <th className="px-2 py-2 text-right">Bonus</th>
                <th className="px-2 py-2 text-right">Deductions</th>
                <th className="px-2 py-2 text-right">Payments</th>
                {deductionCols.map((c) => (
                  <th key={c} className="px-2 py-2 text-right whitespace-nowrap">{c}</th>
                ))}
                <th className="px-2 py-2 text-right">Total Payment</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10 + deductionCols.length} className="p-4 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {error && !loading && (
                <tr><td colSpan={10 + deductionCols.length} className="p-4 text-center text-red-400">{error}</td></tr>
              )}
              {!loading && !error && rows.length === 0 && (
                <tr><td colSpan={10 + deductionCols.length} className="p-4 text-center text-muted-foreground">No saved summaries in this range.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="odd:bg-background even:bg-muted/10">
                  <td className="px-2 py-1 whitespace-nowrap">{ddmmyy(r.week_start)}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{ddmmyy(r.week_end)}</td>
                  <td className="px-2 py-1 text-center">{r.jobs}</td>
                  <td className="px-2 py-1 text-right">{money(r.gross)}</td>
                  <td className="px-2 py-1 text-right text-red-400">-{money(r.gst_amount).replace("$", "")}</td>
                  <td className="px-2 py-1 text-right text-red-400">-{money(r.commission).replace("$", "")}</td>
                  <td className="px-2 py-1 text-right text-green-400">+{money(r.bonus).replace("$", "")}</td>
                  <td className="px-2 py-1 text-right text-red-400">{deductionsFor(r) !== 0 ? "-" : ""}{money(deductionsFor(r)).replace("$", "")}</td>
                  <td className="px-2 py-1 text-right text-green-400">{paymentsFor(r) !== 0 ? "+" : ""}{money(paymentsFor(r)).replace("$", "")}</td>
                  {deductionCols.map((c) => {
                    const v = valueFor(r, c);
                    return (
                      <td key={c} className="px-2 py-1 text-right">
                        {v == null ? "—" : (
                          <span className={v < 0 ? "text-green-400" : "text-red-400"}>
                            {v < 0 ? "+" : "-"}{money(Math.abs(v)).replace("$", "$")}
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1 text-right font-semibold text-green-400">{money(r.total_payment)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0 z-10 bg-neutral-800/95 backdrop-blur border-t border-neutral-700">
              <tr className="font-semibold">
                <td className="px-2 py-2" colSpan={2}>TOTAL</td>
                <td className="px-2 py-2 text-center">{totals.base.jobs}</td>
                <td className="px-2 py-2 text-right">{money(totals.base.gross)}</td>
                <td className="px-2 py-2 text-right">-{money(totals.base.gst).replace("$", "")}</td>
                <td className="px-2 py-2 text-right">-{money(totals.base.commission).replace("$", "")}</td>
                <td className="px-2 py-2 text-right">+{money(totals.base.bonus).replace("$", "")}</td>
                <td className="px-2 py-2 text-right">-{money(totals.base.deductions).replace("$", "")}</td>
                <td className="px-2 py-2 text-right">+{money(totals.base.payments).replace("$", "")}</td>
                {deductionCols.map((c) => (
                  <td key={c} className="px-2 py-2 text-right">{money(totals.ded[c])}</td>
                ))}
                <td className="px-2 py-2 text-right text-green-400">{money(totals.base.payment)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DriverFullReporting;
